import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/common/Combobox';
import { History, Info } from 'lucide-react';

// Deterministic, distinct market colors.
const PALETTE = ['#1d4ed8', '#16a34a', '#dc2626', '#f59e0b', '#9333ea', '#06b6d4', '#db2777', '#65a30d', '#f97316', '#0d9488', '#78350f', '#334155'];
const UNASSIGNED = '#e5e7eb';

const ServiceMarketMap = () => {
  const { token, user } = useContext(AuthContext);
  const isGA = user?.role === 'owner';
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [geo, setGeo] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [hover, setHover] = useState(null); // {county, market_name, x, y}
  const [selected, setSelected] = useState(null); // county name for edit dialog
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/county-markets`, h);
    setAssignments(r.data || []);
  }, [token]);

  useEffect(() => {
    fetch('/fl-counties.json').then((r) => r.json()).then(setGeo).catch(() => {});
    axios.get(`${API}/admin/markets`, h).then((r) => setMarkets(r.data || [])).catch(() => {});
    load();
  }, [load]);

  const marketColor = useMemo(() => {
    const active = markets.filter((m) => m.name !== 'All Markets');
    const map = {};
    active.forEach((m, i) => { map[m.id] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [markets]);

  const byCounty = useMemo(() => {
    const map = {};
    assignments.forEach((a) => { map[a.county] = a; });
    return map;
  }, [assignments]);

  const countyCounts = useMemo(() => {
    const c = {};
    assignments.forEach((a) => { if (a.market_id) c[a.market_id] = (c[a.market_id] || 0) + 1; });
    return c;
  }, [assignments]);

  const fillFor = (county) => {
    const a = byCounty[county];
    if (!a || !a.market_id) return UNASSIGNED;
    return marketColor[a.market_id] || '#94a3b8';
  };

  const openHistory = async (county) => {
    setHistoryFor(county);
    try {
      const r = await axios.get(`${API}/county-markets/${encodeURIComponent(county)}/history`, h);
      setHistoryRows(r.data || []);
    } catch { setHistoryRows([]); }
  };

  const unassignedCount = assignments.filter((a) => !a.market_id).length;

  return (
    <div data-testid="service-market-map">
      <div className="mb-4">
        <p className="text-sm text-slate-600">Each of Florida's 67 counties belongs to one active service market. Hover to inspect; {isGA ? 'click a county to reassign it.' : 'a Global Administrator can reassign counties.'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              {geo ? (
                <svg viewBox={`0 0 ${geo.width} ${geo.height}`} className="mx-auto block h-auto w-full max-h-[70vh]" data-testid="fl-map-svg"
                  onMouseLeave={() => setHover(null)}>
                  {geo.counties.map((c) => {
                    const a = byCounty[c.county];
                    return (
                      <path key={c.county} d={c.d} fill={fillFor(c.county)}
                        stroke="#ffffff" strokeWidth={0.8}
                        data-testid={`county-${c.county.replace(/[^a-zA-Z]/g, '')}`}
                        className={`transition-opacity ${isGA ? 'cursor-pointer' : ''} hover:opacity-80`}
                        onMouseMove={(e) => {
                          const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                          setHover({ county: c.county, market: a?.market_name || 'Unassigned', x: e.clientX - rect.left, y: e.clientY - rect.top });
                        }}
                        onClick={() => isGA && setSelected(c.county)} />
                    );
                  })}
                </svg>
              ) : (
                <div className="py-24 text-center text-slate-400 text-sm">Loading map…</div>
              )}
              {hover && (
                <div className="pointer-events-none absolute z-10 rounded-md bg-[#0E2A47] px-2.5 py-1.5 text-xs text-white shadow-lg"
                  style={{ left: hover.x + 12, top: hover.y + 12 }} data-testid="map-tooltip">
                  <div className="font-semibold">{hover.county} County</div>
                  <div className="text-white/80">{hover.market}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Markets</div>
          <div className="space-y-1.5" data-testid="map-legend">
            {markets.filter((m) => m.name !== 'All Markets').map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-sm">
                <span className="h-3.5 w-3.5 rounded-sm shrink-0" style={{ backgroundColor: marketColor[m.id] }} />
                <span className="flex-1 text-slate-700">{m.name}</span>
                <span className="text-xs text-slate-400">{countyCounts[m.id] || 0}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm pt-1 border-t border-gray-100 mt-1">
              <span className="h-3.5 w-3.5 rounded-sm shrink-0" style={{ backgroundColor: UNASSIGNED }} />
              <span className="flex-1 text-slate-500">Unassigned</span>
              <span className="text-xs text-slate-400">{unassignedCount}</span>
            </div>
          </div>
          {!isGA && (
            <div className="flex items-start gap-1.5 rounded-md bg-slate-50 p-2.5 text-xs text-slate-500">
              <Info size={13} className="mt-0.5 shrink-0" /> Only a Global Administrator can change county assignments.
            </div>
          )}
        </div>
      </div>

      {selected && (
        <AssignDialog county={selected} current={byCounty[selected]} markets={markets}
          onClose={() => setSelected(null)}
          onHistory={() => { openHistory(selected); setSelected(null); }}
          onSaved={() => { setSelected(null); load(); }} />
      )}

      {historyFor && (
        <Dialog open onOpenChange={(o) => !o && setHistoryFor(null)}>
          <DialogContent className="max-w-md" data-testid="county-history-dialog">
            <DialogHeader><DialogTitle className="text-[#0E2A47]">{historyFor} County — Assignment History</DialogTitle></DialogHeader>
            <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto">
              {historyRows.length === 0 && <p className="text-sm text-slate-400">No prior assignments recorded.</p>}
              {historyRows.slice().reverse().map((r, i) => (
                <div key={i} className="rounded-md border border-gray-100 p-2.5 text-xs">
                  <div className="font-semibold text-slate-700">Was: {r.market_name || 'Unassigned'}</div>
                  <div className="text-slate-400">Changed {r.changed_at ? _fmtDateTime(r.changed_at) : ''}{r.changed_by_name ? ` · by ${r.changed_by_name}` : ''}</div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const AssignDialog = ({ county, current, markets, onClose, onSaved, onHistory }) => {
  const { token } = useContext(AuthContext);
  const [marketId, setMarketId] = useState(current?.market_id || '');
  const [busy, setBusy] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/county-markets/${encodeURIComponent(county)}`, { market_id: marketId || null }, h);
      toast.success(`${county} County updated`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="county-assign-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">{county} County</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-xs text-slate-600">Assigned Service Market</label>
            <SearchableSelect testid="county-market-select"
              options={markets.filter((m) => m.name !== 'All Markets').map((m) => ({ value: m.id, label: m.name }))}
              value={marketId} onChange={setMarketId} placeholder="— Unassigned —" allowClear />
          </div>
          <button onClick={onHistory} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E2A47] hover:underline" data-testid="county-history-btn">
            <History size={13} /> View assignment history
          </button>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="county-assign-save"
            className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : 'Save Assignment'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceMarketMap;
